import { api } from 'boot/axios'
import { Pagination } from 'src/modules/core/helpers/pagination'

/**
 * @typedef {{
 *   page: int,
 *   pageSize: int,
 * }} Pagination
 *
 * @typedef {{
 *   id: string,
 *   patientFileId: string,
 *   text: string,
 *   creationDate: string,
 *   lastUpdateDate: string,
 *   status: string,
 *   author: AuthorData,
 * }} NoteData
 *
 * @typedef {{
 *   identifier: string,
 *   firstName: string,
 *   lastName: string,
 *   name: string,
 * }} AuthorData
 *
 * @typedef {{
 *   id: ?string,
 *   title: !string,
 *   dateRange: !string,
 *   position: !int,
 *   size: !int,
 *   ucumUnitCode: !string,
 *   visibleForPatient: !boolean
 *   chartDisplayIndicatorSettings: !ChartDisplayIndicatorSettings[]
 * }} ChartDisplay
 *
 * @typedef {{
 *   id: ?string,
 *   indicatorId: !string,
 *   color: !string,
 *   showAlertTriggers: !boolean,
 *   showReferenceValue: !boolean,
 *   showMin: !boolean,
 *   showMax: !boolean,
 *   showMedian: !boolean,
 *   showAverage: !boolean
 * }} ChartDisplayIndicatorSettings
 *
 * @typedef {{
 *   id: !string,
 *   title: !string,
 *   dateRange: !string,
 *   position: !int,
 *   size: !int,
 *   ucumUnitCode: !string,
 *   visibleForPatient: !boolean
 *   excluded: !boolean
 *   protocol: ?ProtocolData
 *   chartDisplayIndicatorSettings: !ChartDisplayIndicatorSettingsData[]
 * }} ChartDisplayData
 *
 * @typedef {{
 *   id: !string,
 *   indicator: !string,
 *   color: !string,
 *   showAlertTriggers: !boolean,
 *   showReferenceValue: !boolean,
 *   showMin: !boolean,
 *   showMax: !boolean,
 *   showMedian: !boolean,
 *   showAverage: !boolean
 * }} ChartDisplayIndicatorSettingsData
 *
 * @typedef {{
 *   id: !string,
 *   name: !string
 * }} ProtocolData
 *
 * @typedef {{
 *   id: !string,
 *   name: !string
 * }} IndicatorData
 *
 * @typedef {{
 *   activeGlobalReferenceValue: !number,
 *   activeCustomReferenceValueId: !number,
 *   activeCustomReferenceValue: !number,
 *   referenceValueCustomization: !boolean,
 *   status: string,
 *   type: !IndicatorTypeData,
 *   measureType: string,
 *   ucumBaseUnit: !UcumUnit,
 *   compatibleUcumUnits: !UcumUnit[],
 *   id: string,
 *   name: string,
 * }} CustomizableIndicatorData
 *
 * @typedef {{
 *   id: string,
 *   name: string
 * }} IndicatorTypeData
 *
 * @typedef {{
 *   code: string,
 *   symbol: string,
 *   name: !string
 * }} UcumUnit
 *
 * @typedef {{
 *   id: !string,
 *   periodInDays: !int,
 *   indicators: !Indicator[],
 *   status: !string,
 *   overridden: ?IndicatorNonObservanceRule,
 *   isExcluded: !boolean,
 *   protocol: ?Protocol
 * }} IndicatorNonObservanceRule
 *
 * @typedef {{
 *   id: string,
 *   name: string,
 *   indicatorType: ?IndicatorType,
 * }} Indicator
 *
 * @typedef {{
 *   id: string,
 *   name: string
 * }} IndicatorType
 *
 * @typedef {{
 *   id: !string,
 *   name: !string
 * }} Protocol
 *
 * @typedef {{
 *   identifier: string,
 *   firstName: string,
 *   lastName: string,
 *   name: string,
 *   type: 'professional'
 * }} User
 *
 * @typedef {{
 *   id: string,
 *   title: string,
 *   message: string,
 *   endDate: string,
 *   cancellationDate: ?string,
 *   cancelledBy: ?User
 * }} BroadcastMessage
 *
 * @typedef {{
 *   id: !string,
 *   name: !string,
 *   nameSlug: !string,
 *   status: !string,
 *   type: ?IndicatorType,
 *   measureType: !string,
 *   ucumBaseUnit: ?UcumUnit,
 *   molecularWeight: ?number,
 *   compatibleUcumUnits: !UcumUnit[],
 * }} ConditionIndicator
 *
 * @typedef {{
 *   id: !string,
 *   label: !string,
 *   alertLevel: !string,
 *   status: !string,
 *   indicator: !ConditionIndicator,
 *   establishmentServiceId: !string,
 *   patientFileId: ?string,
 *   protocolId: ?string
 * }} PatientFileCondition
 *
 * @typedef {{
 *   data: !Array<PatientFileCondition>,
 *   totalItems: !number
 * }} PatientFileConditionsPage
 */

export function readPatientFile(patientFileId, urlParam = '', filters = {}) {
  const params = {
    ...filters || {}
  }

  return api.get(`/patient_files/${patientFileId}${urlParam}`, { params })
}
export function updatePatientFile(patientFileId, data) {
  return api.put(`/patient_files/${patientFileId}`, data)
}


export function getFollowUp(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/follow-up`)
}

export function createDocument(patientFileId, data = {}, source = 'UPLOADED_CONTENT', providedAsCaregiver = false) {
  return api.post(`/patient_files/${patientFileId}/documents`, {
    ...data,
    source,
    providedAsCaregiver
  })
}

export function createDocumentFromFile(patientFileId, fileId, data = {}, source = 'UPLOADED_CONTENT', providedAsCaregiver = false) {
  return api.post(`/patient_files/${patientFileId}/documents/from_file/${fileId}`, {
    ...data,
    source,
    providedAsCaregiver
  })
}

export function exportPatientFile(patientFileId, data) {
  return api.post(`/patient_files/${patientFileId}/export/pdf`, data)
}

/**
 * @param {string} patientFileId
 * @param {{
 *     indicatorsId?: ?string[],
 *     indicatorsObservationCode?: ?string[],
 *     ucumUnitCode?: ?string,
 *     startDate?: ?string,
 *     endDate?: ?string,
 *     maxResultCount?: ?int,
 *     withThresholds?: ?boolean,
 *     withIndicatorBoundaries?: ?boolean,
 *     withReferenceValues?: ?boolean
 * }} filters
 */
export function getPatientFileIndicatorData(patientFileId, filters = {}) {
  return api.post(`/patient_files/${patientFileId}/indicators/data`, filters)
}

export function getPatientFileIndicatorValuesTableSetting(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/indicator_values_table_settings`)
}

export function upsertPatientFileIndicatorValuesTableSetting(patientFileId, indicators, startDate) {
  return api.put(`/patient_files/${patientFileId}/indicator_values_table_settings`, {
    indicators,
    startDate
  })
}

export function activatePatientData(patientFileId) {
  return api.post(`/patient_files/${patientFileId}/patient_data/activate`)
}

export function deactivatePatientData(patientFileId) {
  return api.post(`/patient_files/${patientFileId}/patient_data/deactivate`)
}

export function closePatientFile(patientFileId, closingReasonType, closingReason) {
  return api.post(`/patient_files/${patientFileId}/close`, { closingReasonType, closingReason })
}

export function reopenPatientFile(patientFileId) {
  return api.post(`/patient_files/${patientFileId}/reopen`)
}

export function listMyPatientFiles(pagination) {
  return api.get('/me/patient_files', {
    params: {
      page: pagination.page,
      pageSize: pagination.rowsPerPage
    }
  })
}

export async function listPatientFilesByPatient(patientId) {
  return await api.get(`/patients/${patientId}/patient_files`)
}

export function listEstablishmentPatientFiles(
  establishmentIdentifier,
  page = 1,
  pageSize = Pagination.rowsPerPage,
  params = {},
  signal = null
) {
  return api.post(`/establishments/${establishmentIdentifier}/patient_files/search`, {
    page,
    pageSize,
    ...params
  }, { signal })
}

export function createPatientFile(patientFile) {
  return api.post('/patient_files', patientFile)
}

export function linkFormToPatientFile(patientFile, formId, params) {
  return api.post(`/patient_files/${patientFile.id}/forms/${formId}`, params)
}

export function deactivatePatientFileForm(patientFileForm) {
  return api.post(`/patient_file_forms/${patientFileForm.id}/deactivate`)
}

export function activatePatientFileForm(patientFileForm) {
  return api.post(`/patient_file_forms/${patientFileForm.id}/activate`)
}

export function overloadPatientFileForm(patientFileFormId, data) {
  return api.post(`/patient_file_forms/${patientFileFormId}/overload`, data)
}

export function activatePatientFileProtocol(protocol) {
  return api.post(`/patient_file_protocols/${protocol.id}/activate`)
}

export function deactivatePatientFileProtocol(patientFileProtocol) {
  return api.post(`/patient_file_protocols/${patientFileProtocol.id}/deactivate`)
}

export function restorePatientFileProtocol(patientFileProtocol) {
  return api.post(`/patient_file_protocols/${patientFileProtocol.id}/restore`)
}

export function applyProtocolToPatientFile(protocolId, patientFileId) {
  return api.post(`/patient_files/${patientFileId}/protocols/${protocolId}/apply`)
}

/**
 * @param {string} patientFileId
 * @returns {Promise<import('axios').AxiosResponse<PatientFileConditionsPage>>}
 */
export function conditionList(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/conditions`)
}

export function boundariesList(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/indicator_boundaries`)
}

export function updateBoundary(patientFileId, indicatorId, data) {
  return api.put(`/patient_files/${patientFileId}/indicators/${indicatorId}/boundaries`, data)
}

export function deleteBoundary(patientFileId, indicatorId, data) {
  return api.delete(`/patient_files/${patientFileId}/indicators/${indicatorId}/boundaries`, { params: data })
}

export function excludePatientFileDocument(patientFileId, documentId) {
  return api.post(`/patient_files/${patientFileId}/documents/${documentId}/exclude`)
}

export function restorePatientFileDocument(patientFileId, documentId) {
  return api.post(`/patient_files/${patientFileId}/documents/${documentId}/restore`)
}

export function listAllPayloads(patientFileId, pagination) {
  return api.get(`/patient_files/${patientFileId}/payloads`, {
    params: {
      page: pagination.page,
      pageSize: pagination.pageSize
    }
  })
}

export function viewCpiaPayload(patientFileId, cpiaPayloadId) {
  return api.get(`/patient_files/${patientFileId}/cpia_payloads/${cpiaPayloadId}`)
}

export function viewMedialPayload(patientFileId, medialPayloadId) {
  return api.get(`/patient_files/${patientFileId}/medial_payloads/${medialPayloadId}`)
}

export function viewDialysisSessionPayload(patientFileId, payloadId) {
  return api.get(`/patient_files/${patientFileId}/dialysis_session_payloads/${payloadId}`)
}

export function excludePatientFileCalculation(patientFileId, calculationLinkId) {
  return api.post(`/patient_files/${patientFileId}/calculation_links/${calculationLinkId}/exclude`)
}

export function restorePatientFileCalculation(patientFileId, calculationLinkId) {
  return api.post(`/patient_files/${patientFileId}/calculation_links/${calculationLinkId}/restore`)
}

/* Données externes biologies */
export function viewBiologyPayload(patientFileId, clinicalPayloadId) {
  return api.get(`/patient_files/${patientFileId}/clinical_document_payloads/${clinicalPayloadId}`)
}

export function linkMeasureToIndicator(patientFileId, clinicalDocumentPayloadId, measureMappingId, indicatorId = null, ucumUnitCode) {
  return api.put(`/patient_files/${patientFileId}/clinical_document_payloads/${clinicalDocumentPayloadId}/measure_mapping/${measureMappingId}`, {
    indicatorId,
    ucumUnitCode
  })
}

export function cancelMeasureMapping(patientFileId, clinicalDocumentPayloadId, measureMappingId) {
  return api.delete(`/patient_files/${patientFileId}/clinical_document_payloads/${clinicalDocumentPayloadId}/measure_mapping/${measureMappingId}`)
}

export function applyMeasureMapping(patientFileId, clinicalDocumentPayloadId) {
  return api.post(`/patient_files/${patientFileId}/clinical_document_payloads/${clinicalDocumentPayloadId}/apply_mapping`)
}

export function getMeasureMappingSuggestions(patientFileId, clinicalDocumentPayloadId, measureMappingId) {
  return api.get(`/patient_files/${patientFileId}/clinical_document_payloads/${clinicalDocumentPayloadId}/mapping_suggestions/${measureMappingId}`)
}

export function listChartDisplays(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/chart_displays`)
}

export function listPatientChartDisplays(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/chart_displays/patient`)
}

/**
 * @param {string} patientFileId
 * @param {ChartDisplay} chartDisplayInput
 * @return {Promise<{data: ChartDisplayData}>}
 */
export function createChartDisplay(patientFileId, chartDisplayInput) {
  return api.post(`/patient_files/${patientFileId}/chart_displays`, chartDisplayInput)
}

/**
 * @param {string} patientFileId
 * @param {string} chartDisplayId
 * @param {ChartDisplay} chartDisplayInput
 * @return {Promise<{data: ChartDisplayData}>}
 */
export function updateChartDisplay(patientFileId, chartDisplayId, chartDisplayInput) {
  return api.put(`/patient_files/${patientFileId}/chart_displays/${chartDisplayId}`, chartDisplayInput)
}

/**
 * @param {string} patientFileId
 * @param {string} chartDisplayId
 * @return {Promise<{data: null}>}
 */
export function excludeChartDisplay(patientFileId, chartDisplayId) {
  return api.post(`/patient_files/${patientFileId}/chart_displays/${chartDisplayId}/exclude`)
}

/**
 * @param {string} patientFileId
 * @param {string} chartDisplayId
 * @return {Promise<{data: null}>}
 */
export function restoreChartDisplay(patientFileId, chartDisplayId) {
  return api.post(`/patient_files/${patientFileId}/chart_displays/${chartDisplayId}/restore`)
}

/**
 * @param {string} patientFileId
 * @param {string} chartDisplayId
 * @return {Promise<{data: null}>}
 */
export function deleteChartDisplay(patientFileId, chartDisplayId) {
  return api.delete(`/patient_files/${patientFileId}/chart_displays/${chartDisplayId}`)
}

/**
 * @typedef {{
 *   type: string,
 *   name: string,
 *   startDate: string,
 *   endDate: ?string,
 *   payload: Object|RendezVous
 * }} CalendarEvent
 */

/**
 * @typedef {{
 *   id: string,
 *   startDate: string,
 *   endDate: string | null,
 *   timezone: string,
 *   title: string,
 *   description: string,
 *   sharedToProfessionals: boolean,
 *   reminderOffset: string,
 *   reminderNote: string,
 *   createdBy: {
 *     identifier: string,
 *     name: string,
 *     firstName: string,
 *     lastName: string,
 *     type: string
 *   },
 *   type: string
 * }} RendezVous
 */

/**
 *
 * @param patientFileId
 * @param filters
 * @param pagination
 * @return {Promise<import('axios').AxiosResponse<CalendarEvent[]>>}
 */
export function listCalendarEvents(patientFileId, filters = {}, pagination = {}) {
  const params = {
    ...filters,
    ...pagination
  }
  return api.get(`/patient_files/${patientFileId}/calendar_events`, { params })
}

export function generateIcsFileUrl(patientFileId, rdvId) {
  return api.get(`/patient_files/${patientFileId}/calendar_events/events/${rdvId}/generate-ical-download-url`)
}

export function createRdv(patientFileId, data = {}) {
  return api.post(`/patient_files/${patientFileId}/calendar_events/rendez_vous`, data)
}

export function updateRdv(patientFileId, rdvId, data) {
  return api.put(`/patient_files/${patientFileId}/calendar_events/rendez_vous/${rdvId}`, data)
}

export function deleteRdv(patientFileId, rdvId) {
  return api.delete(`/patient_files/${patientFileId}/calendar_events/rendez_vous/${rdvId}`)
}

export function createReminder(patientFileId, data = {}) {
  return api.post(`/patient_files/${patientFileId}/calendar_events/reminder`, data)
}

export function updateReminder(patientFileId, reminderId, data) {
  return api.put(`/patient_files/${patientFileId}/calendar_events/reminder/${reminderId}`, data)
}

export function deleteReminder(patientFileId, reminderId) {
  return api.delete(`/patient_files/${patientFileId}/calendar_events/reminder/${reminderId}`)
}

/**
 * @param {string} patientFileId
 * @param {Pagination} pagination
 * @returns {Promise<{data: NoteData[]}>}
 */
export function listNotes(patientFileId, pagination) {
  return api.get(`/patient_files/${patientFileId}/notes`, {
    params: {
      page: pagination.page,
      pageSize: pagination.pageSize
    }
  })
}

/**
 * @param {string} patientFileId
 * @param {string} text
 * @returns {Promise<{data: NoteData[]}>}
 */
export function addNote(patientFileId, text) {
  return api.post(`/patient_files/${patientFileId}/notes`, { text })
}

/**
 * @param {string} patientFileId
 * @param {string} noteId
 * @param {string} text
 * @returns {Promise<{data: NoteData[]}>}
 */
export function editNote(patientFileId, noteId, text) {
  return api.put(`/patient_files/${patientFileId}/notes/${noteId}`, { text })
}

/**
 * @param {string} patientFileId
 * @param {string} noteId
 * @returns {Promise<{data: NoteData}>}
 */
export function archiveNote(patientFileId, noteId) {
  return api.put(`/patient_files/${patientFileId}/notes/${noteId}/archive`)
}

/**
 * @param {string} patientFileId
 * @returns {Promise<{
 *   data: {
 *     data: CustomizableIndicatorData[],
 *     totalItems: int
 *   }
 * }>}
 */
export function listCustomizableIndicators(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/customizable_indicators?pageSize=100`)
}

export function createCustomActivity(patientFileId, activity) {
  return api.post(`/patient_files/${patientFileId}/custom_activities`, activity)
}

export function getCustomActivityTypes(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/custom_activities/types`)
}

/**
 *
 * @param patientFileId
 * @param pagination
 * @param {{
 *   fileExtension: string,
 *   withExcludedProtocolDocuments: boolean,
 *   groupedItems: boolean
 * }} filters
 * @return {Promise<import('axios').AxiosResponse<any>>}
 */
export function getTherapeuticContent(patientFileId, pagination, filters = {}) {
  return api.get(`/patient_files/${patientFileId}/content`, {
    params: {
      ...pagination,
      submissionTypes: ['FREE'],
      withDocuments: true,
      ...filters
    }
  })
}

/**
 * @param patientFileId
 * @returns {Promise<import('axios').AxiosResponse<IndicatorNonObservanceRule[]>>}
 */
export function listIndicatorNonObservanceRules(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/indicator_non_observance_rules`)
}

/**
 * @param patientFileId
 * @param indicatorIds
 * @param periodInDays
 * @returns {Promise<import('axios').AxiosResponse<IndicatorNonObservanceRule>>}
 */
export function createIndicatorNonObservanceRule(patientFileId, indicatorIds, periodInDays) {
  return api.post(`/patient_files/${patientFileId}/indicator_non_observance_rules`, {
    indicators: indicatorIds,
    periodInDays
  })
}

/**
 * @param patientFileId
 * @param ruleId
 * @param indicatorIds
 * @param periodInDays
 * @returns {Promise<import('axios').AxiosResponse<IndicatorNonObservanceRule>>}
 */
export function updateIndicatorNonObservanceRule(patientFileId, ruleId, indicatorIds, periodInDays) {
  return api.put(`/patient_files/${patientFileId}/indicator_non_observance_rules/${ruleId}`, {
    indicators: indicatorIds,
    periodInDays
  })
}

export function deactivateIndicatorNonObservanceRule(patientFileId, ruleId) {
  return api.post(`/patient_files/${patientFileId}/indicator_non_observance_rules/${ruleId}/deactivate`)
}

export function excludeIndicatorNonObservanceRule(patientFileId, ruleId) {
  return api.post(`/patient_files/${patientFileId}/indicator_non_observance_rules/${ruleId}/exclude`)
}

export function restoreIndicatorNonObservanceRule(patientFileId, ruleId) {
  return api.post(`/patient_files/${patientFileId}/indicator_non_observance_rules/${ruleId}/restore`)
}

/**
 * @param patientFileId
 * @param ruleId
 * @param indicatorIds
 * @param periodInDays
 * @returns {Promise<import('axios').AxiosResponse<IndicatorNonObservanceRule>>}
 */
export function overrideIndicatorNonObservanceRule(patientFileId, ruleId, indicatorIds, periodInDays) {
  return api.post(`/patient_files/${patientFileId}/indicator_non_observance_rules/${ruleId}/override`, {
    indicators: indicatorIds,
    periodInDays
  })
}

/**
 * @param {string} patientFileId
 * @returns {Promise<import('axios').AxiosResponse<BroadcastMessage[]>>}
 */
export function listUnreadBroadcastMessages(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/broadcast_messages`)
}

/**
 * @param {string} patientFileId
 * @param {string[]} broadcastMessageIds
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export function hideBroadcastMessages(patientFileId, broadcastMessageIds) {
  return api.post(`/patient_files/${patientFileId}/broadcast_messages/hide`, { broadcastMessages: broadcastMessageIds })
}

export function setReferringDoctor(patientFileId, referringDoctorId = null) {
  return api.put(`/patient_files/${patientFileId}/referring_doctor`, { referringDoctor: referringDoctorId })
}

/**
 * @param {string} establishmentId
 * @param {Pagination} pagination
 * @param {{
 *   serviceIds?: string[],
 *   referringDoctor?: string,
 * }} filters
 * @return {Promise<import('axios').AxiosResponse<any>>}
 */
export function listPatientFilesWithoutPrescriptions(
  establishmentId,
  pagination,
  filters = {}
) {
  return api.get(
    `/establishments/${establishmentId}/patient-files/available-for-first-prescription`,
    {
      params: {
        ...pagination,
        ...filters
      }
    }
  )
}

/**
 * @param {string} patientFileId
 * @return {Promise<import('axios').AxiosResponse<{prescriptionWeight: ?string}>>}
 */
export function getPrescriptionWeight(patientFileId) {
  return api.get(`/patient_files/${patientFileId}/prescription_weight`)
}

/**
 * @typedef {{
 *  indicator: string,
 *  value: string,
 *  ucumUnitCode: ?string
 * }} MeasureInput
 */

/**
 * @param patientFileId {string}
 * @param title {string}
 * @param effectiveDate {string} iso format
 * @param generateAlerts {boolean}
 * @param manualMeasureInputs {MeasureInput[]}
 */
export function createManualInput(patientFileId, title, effectiveDate, generateAlerts, manualMeasureInputs) {
  return api.post(`/patient_files/${patientFileId}/manual_inputs`, {
    title,
    effectiveDate,
    generateAlerts,
    manualMeasureInputs
  })
}

export function saveCognitiveExerciseResults(patientFileId, data) {
  return api.post(`/patient_files/${patientFileId}/cognitive_exercise_payloads`, data)
}

export function saveConcussionFollowUpFormAnswer(patientFileId, data) {
  return api.post(`/patient_files/${patientFileId}/concussion_follow_up_form_payloads`, data)
}
